/**
 * Punkt 101, Stufe 1 (docs/KONZEPT-VORMERKUNGEN.md § 1 Punkt 1): „Kunden → Vormerkungen“.
 * Die stündlich von der Website abgeholten Saison-Vormerkungen, gruppiert nach Anlass und Jahr
 * (Chips mit Anzahl, z. B. „Silvester 2026 · 38“), nur lesen. Listenstandard
 * (docs/PLAN-listenstandard.md): `useListSort` + `SortierKopf` + `ListFooter`, Lade-/Fehler-/
 * Leerzustand wie die Kundenliste.
 *
 * Stufe 2 (Mockup a/b, freigegeben 25.09.2026): je Anlass und Jahr eine Karte mit der Saison-
 * Kampagne (anlegen/ansehen im Drawer `SaisonKampagneDrawer`).
 *
 * Stufe 3 (Mockup d, freigegeben 26.09.2026): Kennzahl „Vormerkung → Buchung“ auf der Karte,
 * Spalte „Rücklauf“ je Vormerkung und ein kleiner Knopf „Anfrage machen“ für Gäste, die
 * angerufen statt geantwortet haben (`AnfrageAusVormerkungDrawer`).
 *
 * Saison-Mails (Antoine 26.09.2026, Entscheidung 5): ein neues Saisonmenü auf der Website erscheint
 * hier NUR als Hinweis „Neues Menü erkannt – Kampagne anlegen?“ — es wird dadurch nichts verschickt.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiMutate, useApi } from "../hooks/useApi";
import { useListSort, type SortSpalte } from "../hooks/useListSort";
import { formatDate, PILL_CLASSES } from "../ui/format";
import { Button, Card, EmptyState, ErrorNote, Icon, LISTEN_KOPF_ZELLE, ListFooter, LoadingRows, PageHeader, SortierKopf } from "../ui/components";
import { SaisonKampagneDrawer, anlassName, stichtagVorschlag, tagMonat, type SaisonKampagne } from "../ui/SaisonKampagneDrawer";

/** GET /api/saison-kampagnen/hinweise (Migration 539). */
export interface MenueHinweis {
  id: string;
  anlass: string;
  jahr: number;
  titel: string;
  erkanntAt: string;
}
import { AnfrageAusVormerkungDrawer } from "../ui/AnfrageAusVormerkungDrawer";
import { vorgangPfad } from "../lib/pfade";

export interface Vormerkung {
  id: string;
  email: string;
  sprache: string;
  anlass: string;
  jahr: number;
  status: "aktiv" | "abgemeldet";
  bestaetigtAt: string;
  einwilligungAt: string;
  einwilligungText: string;
  einwilligungIp: string | null;
  einwilligungVersion: string | null;
  abgemeldetAt: string | null;
  anlassDatum: string;
  ruecklauf: {
    angeschriebenAm: string | null;
    geantwortetAm: string | null;
    inquiryId: string | null;
    eventId: string | null;
    eventNummer: string | null;
    gebucht: boolean;
    ueberFormular: boolean;
  };
}

const ANLASS: Record<string, string> = {
  valentinstag: "Valentinstag",
  ostermontag: "Ostermontag",
  weihnachten: "Weihnachten",
  silvester: "Silvester",
};
export const anlassLabel = (anlass: string, jahr: number) => `${ANLASS[anlass] ?? anlass} ${jahr}`;

const KENNZAHL_ERKLAERUNG =
  "Basis: Vormerkungen dieser Saison, an die der Vorschlag verschickt wurde (vor dem Versand Abgemeldete zählen nicht). " +
  "Geantwortet: danach kam eine Mail von derselben Adresse. Anfrage: über „Daraus Anfrage machen“ verknüpft oder über das " +
  "Website-Formular mit derselben Adresse und Datum am Anlass. Gebucht: Vorgang Zugesagt, Bezahlt oder Durchgeführt — " +
  "abgesagt fällt wieder heraus. Quote = gebucht ÷ angeschrieben.";

const ZELLE = "border-t border-[#F6F0E4] px-[7px] py-3 align-middle first:pl-[18px] last:pr-[18px] dark:border-white/[0.06]";

const SPALTEN: SortSpalte<Vormerkung>[] = [
  { key: "email", label: "E-Mail", type: "text", accessor: (v) => v.email },
  { key: "anlass", label: "Anlass", type: "text", accessor: (v) => anlassLabel(v.anlass, v.jahr) },
  { key: "sprache", label: "Sprache", type: "text", accessor: (v) => v.sprache },
  { key: "bestaetigt", label: "Bestätigt", type: "date", accessor: (v) => v.bestaetigtAt },
  { key: "status", label: "Status", type: "text", accessor: (v) => v.status },
  { key: "ruecklauf", label: "Rücklauf", type: "number", accessor: (v) => ruecklaufStufe(v) },
];

/** Reihenfolge fürs Sortieren: gebucht > Anfrage > geantwortet > angeschrieben > nichts. */
function ruecklaufStufe(v: Vormerkung): number {
  const r = v.ruecklauf;
  return r.gebucht ? 4 : r.inquiryId ? 3 : r.geantwortetAm ? 2 : r.angeschriebenAm ? 1 : 0;
}

function RuecklaufPille({ v }: { v: Vormerkung }) {
  const r = v.ruecklauf;
  const [text, ton] = r.gebucht
    ? [`Gebucht · ${r.ueberFormular ? "über Formular" : (r.eventNummer ?? "Vorgang")}`, "success" as const]
    : r.inquiryId
      ? [`Anfrage ${r.eventNummer ?? ""}`.trim(), "warn" as const]
      : r.geantwortetAm
        ? [`Geantwortet ${tagMonat(r.geantwortetAm.slice(0, 10))}`, "info" as const]
        : r.angeschriebenAm
          ? [`Angeschrieben ${tagMonat(r.angeschriebenAm.slice(0, 10))}`, "neutral" as const]
          : [null, null];
  if (!text) return <span className="text-secondary dark:text-secondary-fixed-dim">—</span>;
  const pille = (
    <span
      className={`inline-flex max-w-full truncate rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${PILL_CLASSES[ton!]}`}
      title={r.ueberFormular ? "Über das Website-Formular, ohne Antwort auf die Mail — gleiche E-Mail, nach dem Versand, Datum am Anlass" : undefined}
    >
      {text}
    </span>
  );
  return r.eventId ? <Link to={vorgangPfad(r.eventId)}>{pille}</Link> : pille;
}

function nachweis(v: Vormerkung): string {
  return [
    `Einwilligung am ${formatDate(v.einwilligungAt)}`,
    v.einwilligungIp ? `IP ${v.einwilligungIp}` : null,
    v.einwilligungVersion ? `Fassung ${v.einwilligungVersion}` : null,
    `„${v.einwilligungText}“`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Karte je Anlass und Jahr (Mockup a): Zahl, Sprachen, Kampagnenstand, ein Knopf. */
function KampagnenKarte({
  anlass,
  jahr,
  vormerkungen,
  kampagne,
  heute,
  onOeffnen,
}: {
  anlass: string;
  jahr: number;
  vormerkungen: Vormerkung[];
  kampagne: SaisonKampagne | null;
  heute: string;
  onOeffnen: () => void;
}) {
  const aktiv = vormerkungen.filter((v) => v.status === "aktiv");
  const en = aktiv.filter((v) => v.sprache.toLowerCase().startsWith("en")).length;
  const abgemeldet = vormerkungen.length - aktiv.length;
  const status = !kampagne
    ? { text: "Noch keine Kampagne", ton: "neutral" as const, icon: null }
    : heute < kampagne.stichtag
      ? { text: "Geplant", ton: "info" as const, icon: "schedule" }
      : kampagne.offen > 0
        ? { text: `${kampagne.offen} bereit zur Freigabe`, ton: "warn" as const, icon: "outgoing_mail" }
        : { text: "Vorbereitet", ton: "success" as const, icon: "check" };
  const zeile = "flex items-center gap-1.5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim";
  return (
    <Card className="flex flex-col gap-2 p-[18px]" data-testid={`kampagnen-karte-${anlass}-${jahr}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h4 className="font-headline-sm text-[15px] font-bold text-on-background dark:text-inverse-on-surface">{anlassLabel(anlass, jahr)}</h4>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${PILL_CLASSES[status.ton]}`}>
          {status.icon && <Icon name={status.icon} className="text-[13px]" />}
          {status.text}
        </span>
      </div>
      <div>
        <span className="font-headline-md text-[26px] font-bold tabular-nums text-on-background dark:text-inverse-on-surface">{aktiv.length}</span>{" "}
        <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{aktiv.length === 1 ? "aktive Vormerkung" : "aktive Vormerkungen"}</span>
      </div>
      <div className={zeile}>
        <Icon name="translate" className="text-[15px]" />
        {`${aktiv.length - en} Deutsch · ${en} Englisch${abgemeldet ? ` · ${abgemeldet} abgemeldet` : ""}`}
      </div>
      {kampagne ? (
        <>
          <div className={zeile}>
            <Icon name="restaurant_menu" className="text-[15px]" />
            {kampagne.paketName ?? "Paket gelöscht — bitte neu wählen"}
          </div>
          <div className={zeile}>
            <Icon name="event" className="text-[15px]" />
            {heute < kampagne.stichtag
              ? `Vorschläge werden am ${tagMonat(kampagne.stichtag)} vorbereitet`
              : `Am ${tagMonat(kampagne.stichtag)} vorbereitet · ${kampagne.versendet} verschickt`}
          </div>
        </>
      ) : (
        <div className={zeile}>
          <Icon name="info" className="text-[15px]" />
          {`Vorschlag für den Stichtag: ${formatDate(stichtagVorschlag(anlass, jahr))}`}
        </div>
      )}
      {kampagne?.kennzahl && (kampagne.kennzahl.angeschrieben > 0 ? (
        <div className="mt-1 rounded-xl bg-surface-container-high/50 p-3 dark:bg-white/[0.03]" data-testid={`kennzahl-${anlass}-${jahr}`}>
          <div className="flex items-baseline gap-2">
            <span className="font-headline-md text-[22px] font-bold tabular-nums text-on-background dark:text-inverse-on-surface">{kampagne.kennzahl.quoteProzent} %</span>
            <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Vormerkung → Buchung</span>
            <span title={KENNZAHL_ERKLAERUNG} className="cursor-help text-secondary">
              <Icon name="help" className="text-[15px]" />
            </span>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-1 text-center">
            {(
              [
                [kampagne.kennzahl.angeschrieben, "angeschrieben"],
                [kampagne.kennzahl.geantwortet, "geantwortet"],
                [kampagne.kennzahl.anfragen, "Anfragen"],
                [kampagne.kennzahl.gebucht, "gebucht"],
              ] as const
            ).map(([n, l]) => (
              <div key={l}>
                <div className="font-body-sm text-[15px] font-bold tabular-nums text-on-background dark:text-inverse-on-surface">{n}</div>
                <div className="font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim">{l}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={zeile}>
          <Icon name="query_stats" className="text-[15px]" />
          Kennzahl erscheint, sobald Vorschläge verschickt sind
        </div>
      ))}
      <div className="mt-auto flex flex-wrap gap-2 pt-2">
        {kampagne && kampagne.offen > 0 && (
          <Link to="/freigaben" className="inline-flex items-center gap-1 font-body-sm text-body-sm font-semibold text-primary hover:underline">
            Zu den Freigaben
          </Link>
        )}
        <Button variant={kampagne ? "secondary" : "primary"} className="ml-auto !py-1.5" onClick={onOeffnen}>
          {!kampagne && <Icon name="campaign" className="text-[16px]" />}
          {kampagne ? "Kampagne ansehen" : "Saison-Kampagne anlegen"}
        </Button>
      </div>
    </Card>
  );
}

export default function VormerkungenPage() {
  const { data, loading, error, reload } = useApi<Vormerkung[]>("/api/vormerkungen");
  const kampagnenQ = useApi<SaisonKampagne[]>("/api/saison-kampagnen");
  const hinweiseQ = useApi<MenueHinweis[]>("/api/saison-kampagnen/hinweise");
  const alle = useMemo(() => data ?? [], [data]);
  const [gruppe, setGruppe] = useState<string>("alle");
  const [drawer, setDrawer] = useState<{ anlass: string; jahr: number } | null>(null);
  const [anrufer, setAnrufer] = useState<Vormerkung | null>(null);
  const heute = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Berlin" });
  const kampagneVon = (anlass: string, jahr: number) => (kampagnenQ.data ?? []).find((k) => k.anlass === anlass && k.jahr === jahr) ?? null;

  // Gruppen „Anlass Jahr“ mit Anzahl der aktiven Vormerkungen, neueste Saison zuerst.
  const gruppen = useMemo(() => {
    const m = new Map<string, { key: string; label: string; jahr: number; aktiv: number }>();
    for (const v of alle) {
      const key = `${v.anlass}:${v.jahr}`;
      const g = m.get(key) ?? { key, label: anlassLabel(v.anlass, v.jahr), jahr: v.jahr, aktiv: 0 };
      if (v.status === "aktiv") g.aktiv++;
      m.set(key, g);
    }
    return [...m.values()].sort((a, b) => b.jahr - a.jahr || a.label.localeCompare(b.label, "de"));
  }, [alle]);

  const gefiltert = gruppe === "alle" ? alle : alle.filter((v) => `${v.anlass}:${v.jahr}` === gruppe);
  const sort = useListSort("vormerkungen", SPALTEN);
  // ponytail: keine Virtualisierung (Plan § Grundbefund); nachrüsten, wenn die Liste > ~200 Zeilen wächst.
  const sortiert = sort.sortiere(gefiltert);
  const aktivGesamt = alle.filter((v) => v.status === "aktiv").length;

  const chip = (aktiv: boolean) =>
    `rounded-full px-3.5 py-1.5 font-label-md text-[11.5px] font-semibold ${
      aktiv
        ? "bg-on-background text-inverse-on-surface dark:bg-inverse-on-surface dark:text-inverse-surface"
        : "border border-outline-variant/60 bg-surface-container-lowest text-on-surface-variant dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
    }`;

  return (
    <>
      <PageHeader
        title="Vormerkungen"
        zurueck={{ label: "Kunden", to: "/kunden" }}
        subtitle="Gäste, die sich auf der Website für einen Anlass vorgemerkt und die Anmeldung bestätigt haben. Stündlich abgeglichen, nach 18 Monaten gelöscht."
      />

      {(hinweiseQ.data ?? []).map((h) => (
        <Card key={h.id} data-testid="menue-hinweis" className="mb-4 flex flex-col gap-3 border-primary/30 bg-primary/[0.04] p-[18px] sm:flex-row sm:items-center">
          <Icon name="restaurant_menu" className="shrink-0 text-[26px] text-primary" />
          <div className="min-w-0 flex-1">
            <p className="font-body-md text-body-md font-bold text-on-background dark:text-inverse-on-surface">Neues Menü erkannt – Kampagne anlegen?</p>
            <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
              {`„${h.titel}“ ist auf der Website erschienen (${anlassName(h.anlass)} ${h.jahr}). Es wird nichts verschickt — erst eine Kampagne mit Ihrer Freigabe.`}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              variant="ghost"
              className="!py-1.5"
              onClick={() => void apiMutate(`/api/saison-kampagnen/hinweise/${h.id}/erledigt`, "POST").then(() => hinweiseQ.reload())}
            >
              Ausblenden
            </Button>
            <Button variant="primary" className="!py-1.5" onClick={() => setDrawer({ anlass: h.anlass, jahr: h.jahr })}>
              <Icon name="campaign" className="text-[16px]" /> Kampagne anlegen
            </Button>
          </div>
        </Card>
      ))}

      {gruppen.length > 0 && (
        <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {gruppen.map((g) => {
            const [anlass, jahr] = [g.key.split(":")[0]!, Number(g.key.split(":")[1])];
            return (
              <KampagnenKarte
                key={g.key}
                anlass={anlass}
                jahr={jahr}
                vormerkungen={alle.filter((v) => v.anlass === anlass && v.jahr === jahr)}
                kampagne={kampagneVon(anlass, jahr)}
                heute={heute}
                onOeffnen={() => setDrawer({ anlass, jahr })}
              />
            );
          })}
        </div>
      )}

      {drawer && (() => {
        const aktiv = alle.filter((v) => v.anlass === drawer.anlass && v.jahr === drawer.jahr && v.status === "aktiv");
        const en = aktiv.filter((v) => v.sprache.toLowerCase().startsWith("en")).length;
        return (
          <SaisonKampagneDrawer
            anlass={drawer.anlass}
            jahr={drawer.jahr}
            kampagne={kampagneVon(drawer.anlass, drawer.jahr)}
            anzahl={{ aktiv: aktiv.length, de: aktiv.length - en, en }}
            onClose={() => setDrawer(null)}
            onGespeichert={() => {
              setDrawer(null);
              kampagnenQ.reload();
              hinweiseQ.reload();
            }}
          />
        );
      })()}

      {anrufer && (() => {
        const k = kampagneVon(anrufer.anlass, anrufer.jahr);
        return (
          <AnfrageAusVormerkungDrawer
            vormerkung={{
              vormerkungId: anrufer.id,
              anlass: anrufer.anlass,
              jahr: anrufer.jahr,
              anlassDatum: anrufer.anlassDatum,
              sprache: anrufer.sprache,
              paketName: k?.paketName ?? null,
              preisProPersonCents: k?.preisProPersonCents ?? null,
            }}
            vorbelegung={{ name: "", email: anrufer.email, nachricht: "" }}
            onClose={() => setAnrufer(null)}
          />
        );
      })()}

      {gruppen.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <button type="button" onClick={() => setGruppe("alle")} className={chip(gruppe === "alle")}>
            Alle · {aktivGesamt}
          </button>
          {gruppen.map((g) => (
            <button key={g.key} type="button" onClick={() => setGruppe(g.key)} className={chip(gruppe === g.key)}>
              {g.label} · {g.aktiv}
            </button>
          ))}
        </div>
      )}

      <Card className="overflow-hidden">
        {error ? (
          <ErrorNote zentriert title="Vormerkungen konnten nicht geladen werden" message={error} onRetry={reload} />
        ) : !loading && alle.length === 0 ? (
          <EmptyState
            icon="event_upcoming"
            title="Noch keine Vormerkungen"
            hint="Sobald sich Gäste auf der Website für einen Anlass vormerken und das bestätigen, erscheinen sie hier."
          />
        ) : (
          <div className="max-h-[max(20rem,calc(100vh-19rem))] overflow-auto" aria-busy={loading}>
            <table className="handy-karten w-full min-w-[1000px] table-fixed border-collapse text-left text-[13px]">
              <colgroup>
                <col />
                <col className="w-[170px]" />
                <col className="w-[90px]" />
                <col className="w-[130px]" />
                <col className="w-[120px]" />
                <col className="w-[190px]" />
                <col className="w-[150px]" />
              </colgroup>
              <thead>
                <tr>
                  {SPALTEN.map((sp) => (
                    <SortierKopf key={sp.key} label={sp.label} aktiv={sort.sortKey === sp.key ? sort.dir : null} onClick={() => sort.toggle(sp.key)} />
                  ))}
                  <th className={`${LISTEN_KOPF_ZELLE} uppercase tracking-[0.07em]`}>Aktion</th>
                </tr>
              </thead>
              {loading ? (
                <LoadingRows rows={6} spalten={["text", "text", "text", "text", "pill", "pill", "leer"]} />
              ) : (
                <tbody>
                  {sortiert.map((v) => (
                    <tr key={v.id} className={v.status === "abgemeldet" ? "opacity-55" : ""}>
                      <td className={`${ZELLE} font-semibold text-on-background dark:text-inverse-on-surface`}>
                        <span className="block truncate" title={v.email}>
                          {v.email}
                        </span>
                      </td>
                      <td className={`${ZELLE} text-[#57503F] dark:text-secondary-fixed-dim`}>{anlassLabel(v.anlass, v.jahr)}</td>
                      <td className={`${ZELLE} uppercase text-[#57503F] dark:text-secondary-fixed-dim`}>{v.sprache}</td>
                      <td className={`${ZELLE} whitespace-nowrap tabular-nums text-on-surface-variant dark:text-secondary-fixed-dim`} title={nachweis(v)}>
                        {formatDate(v.bestaetigtAt)}
                      </td>
                      <td className={ZELLE}>
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${PILL_CLASSES[v.status === "aktiv" ? "success" : "neutral"]}`}
                          title={v.abgemeldetAt ? `Abgemeldet am ${formatDate(v.abgemeldetAt)}` : undefined}
                        >
                          {v.status === "aktiv" ? "Aktiv" : "Abgemeldet"}
                        </span>
                      </td>
                      <td className={ZELLE}>
                        <RuecklaufPille v={v} />
                      </td>
                      <td className={ZELLE}>
                        {v.status === "aktiv" && !v.ruecklauf.inquiryId && (
                          <Button variant="secondary" className="whitespace-nowrap !px-2.5 !py-1 !text-[12px]" onClick={() => setAnrufer(v)} title="Für Gäste, die angerufen statt geantwortet haben">
                            <Icon name="add_task" className="text-[14px]" /> Anfrage machen
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        )}
        {!error && (
          <ListFooter
            anzahl={gefiltert.length}
            gesamt={alle.length}
            einheit="Vormerkungen"
            sortiertNach={sort.spalte?.label ?? null}
            richtung={sort.dir}
            onReset={sort.reset}
          />
        )}
      </Card>
    </>
  );
}
